/**
 * syncBatcher - Lógica pura de agrupamiento de cambios pendientes
 *
 * Módulo COMPARTIDO entre la app (SyncManager) y el Service Worker (sw.ts).
 * Es deliberadamente PURO: no importa el cliente de Supabase, no toca `window`
 * ni event emitters. El envío real a Supabase se inyecta vía `BatchSenders`,
 * para que cada contexto (app con stripLocalFields, SW con timeout/token)
 * conserve su mecánica sin duplicar la lógica de agrupamiento.
 *
 * Objetivo: en lugar de procesar los pendientes uno por uno (N requests a
 * Supabase), se agrupan por `table:action` y se envían en una sola operación
 * bulk por chunk. deletes por un lado, inserts/updates por otro.
 */

import { TABLE_CONFIG } from '../configs/tableConfig';
import type { PendingChange } from '../configs/tableConfig';
import { esRechazadoPorPermisos } from './syncErrors';

/**
 * Resultado de un envío individual (un chunk) a Supabase.
 */
export interface SendOutcome {
  ok: boolean;
  error?: string;
}

/**
 * Funciones de envío inyectadas por cada contexto.
 * Cada una recibe un chunk ya recortado a `batchSize` y devuelve si tuvo éxito.
 */
export interface BatchSenders {
  /** Upsert de registros completos (INSERT / UPDATE / BULK_INSERT). */
  upsertRecords(table: string, records: Record<string, any>[]): Promise<SendOutcome>;
  /** Delete masivo por ids (DELETE / BULK_DELETE). */
  deleteByIds(table: string, ids: string[]): Promise<SendOutcome>;
  /** Upsert merge de registros parciales (BULK_UPDATE). */
  bulkUpdateRecords(table: string, records: Record<string, any>[]): Promise<SendOutcome>;
}

export interface FlushOptions {
  /** Tamaño máximo de cada chunk enviado a Supabase. */
  batchSize: number;
}

export interface FlushResult {
  /** IDs de pending changes enviados con éxito (eliminar de la cola). */
  succeededPendingIds: number[];
  /** IDs de pending changes que fallaron (marcar retryCount + lastError). */
  failedPendingIds: number[];
  /**
   * Error del último chunk fallido por grupo `table:action` (ej:
   * `'conceptos:INSERT' → 'duplicate key ...'`). Permite al caller marcar el
   * pending con el error REAL en vez de un genérico, y detectar conflictos
   * (RLS / uniqueness) cuando el retryCount alcanza el máximo.
   */
  groupErrors?: Record<string, string>;
  /**
   * IDs de pending changes NO enviados en esta corrida porque un grupo de una
   * tabla padre (DEPENDENCIAS_SYNC) falló. No se borran ni se marcan: quedan en
   * la cola tal cual y salen en la próxima corrida, detrás de su padre.
   */
  deferredPendingIds?: number[];
}

/**
 * Máximo de reintentos de push para un pending change (compartido app + SW).
 *
 * Al agotarlo, el cambio queda RETENIDO en la cola marcado como CONFLICTO
 * (lastError con prefijo) — JAMÁS se borra: el dato sobrevive hasta que el
 * usuario resuelva el conflicto (ej: renombrar un concepto duplicado) o lo
 * descarte. Evita tanto el retry infinito como la pérdida del cambio.
 */
export const MAX_PUSH_RETRIES = 5;

/**
 * ¿El pendiente quedó RETENIDO en la cola (no se vuelve a enviar)? Agotó
 * MAX_PUSH_RETRIES (CONFLICTO) o fue rechazado por permisos (RLS). Único
 * criterio compartido por SyncManager, el SW y el chequeo de "padre en cola"
 * del push inmediato.
 */
export function esPendienteRetenido(change: Pick<PendingChange, 'retryCount' | 'lastError'>): boolean {
  return (change.retryCount || 0) >= MAX_PUSH_RETRIES || esRechazadoPorPermisos(change.lastError);
}

/**
 * Dependencias de sync tabla hija → tablas padre (lista DECLARATIVA, compartida
 * app + SW).
 *
 * Una tabla hija cuyo INSERT/UPDATE exige que la fila padre YA exista en
 * Supabase (FK no diferible y/o RLS con `EXISTS (... padre ...)`) se declara
 * acá. Efectos:
 *  - `flushPendingBatch`: los grupos de escritura (INSERT/UPDATE/BULK_*) de la
 *    hija se envían DESPUÉS de todos los grupos de escritura de sus padres
 *    presentes en la misma corrida; si alguno de esos grupos padre falla, los de
 *    la hija se difieren (no se envían ni se marcan con error: quedan en la
 *    cola para la próxima corrida).
 *  - `SyncManager` (push inmediato online): el push de la hija espera a los
 *    pushes en vuelo de sus padres y, si el padre quedó en la cola (offline o
 *    fallido), la hija se encola en vez de mandarse sola.
 *
 * Para sumar una tabla hija nueva: agregar `hija: ['padre', ...]` acá (sin
 * ciclos). Los DELETE no se reordenan (las FK hijas usan ON DELETE CASCADE).
 *
 * | Hija            | Padres           | Por qué                                              |
 * |-----------------|------------------|------------------------------------------------------|
 * | siembras_lote          | elementos_mapa | FK elemento_id + policy siembras_lote_insert EXISTS |
 * | puntos_control_eventos | puntos_control | FK punto_id NOT NULL, no diferible (foto rápida)    |
 *
 * Las dependencias son por TABLA, no por registro: si falla una fila padre
 * cualquiera (aunque no tenga nada que ver), las hijas de esa corrida se
 * difieren hasta que esa fila sube o queda retenida (esPendienteRetenido) y se
 * filtra antes del batcher.
 */
export const DEPENDENCIAS_SYNC: Readonly<Record<string, readonly string[]>> = {
  siembras_lote: ['elementos_mapa'],
  puntos_control_eventos: ['puntos_control'],
};

/** Tablas padre declaradas para `table` (vacío si no depende de ninguna). */
export function tablasPadreDeSync(table: string): string[] {
  return [...(DEPENDENCIAS_SYNC[table] ?? [])];
}

const ACCIONES_DE_BORRADO = new Set(['DELETE', 'BULK_DELETE']);
const esGrupoDeEscritura = (key: string) => !ACCIONES_DE_BORRADO.has(key.split(':')[1]);

/**
 * Ordena las claves `table:action` respetando DEPENDENCIAS_SYNC: un grupo de
 * escritura de una tabla hija se posterga hasta que salieron todos los grupos de
 * escritura de sus padres presentes en la corrida. El resto conserva el orden de
 * la cola (orden de inserción del Map = orden de `pending_changes.id`).
 */
function ordenarGruposPorDependencia(keys: string[]): string[] {
  const escrituraPorTabla = new Map<string, number>();
  for (const key of keys) {
    if (!esGrupoDeEscritura(key)) continue;
    const table = key.split(':')[0];
    escrituraPorTabla.set(table, (escrituraPorTabla.get(table) ?? 0) + 1);
  }
  const faltan = (key: string) =>
    esGrupoDeEscritura(key) &&
    tablasPadreDeSync(key.split(':')[0]).some(padre => (escrituraPorTabla.get(padre) ?? 0) > 0);

  const ordenadas: string[] = [];
  const postergadas: string[] = [];
  const emitir = (key: string): void => {
    ordenadas.push(key);
    if (!esGrupoDeEscritura(key)) return;
    const table = key.split(':')[0];
    escrituraPorTabla.set(table, (escrituraPorTabla.get(table) ?? 1) - 1);
    // Liberar (en orden de cola) las postergadas cuyos padres ya salieron todos.
    const i = postergadas.findIndex(p => !faltan(p));
    if (i >= 0) emitir(postergadas.splice(i, 1)[0]);
  };
  for (const key of keys) {
    if (faltan(key)) postergadas.push(key);
    else emitir(key);
  }
  // Defensa ante un ciclo mal declarado: lo que quede sale en orden de cola.
  ordenadas.push(...postergadas);
  return ordenadas;
}

/**
 * Divide un array en chunks de hasta `size` elementos.
 */
function chunkArray<T>(arr: T[], size: number): T[][] {
  if (size <= 0 || arr.length === 0) return [arr];
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
}

/**
 * Fusiona los registros con la misma clave primaria, en ORDEN DE COLA: el posterior se aplica
 * encima del anterior (`Object.assign`) y la fila fusionada queda en la posición de la primera
 * aparición. Postgres rechaza un upsert que toca dos veces la misma fila ("ON CONFLICT DO UPDATE
 * command cannot affect row a second time") y con eso caería el grupo entero — p. ej. dos
 * correcciones offline de la misma siembra. Los registros sin clave pasan tal cual.
 *
 * Los pendientes fusionados no necesitan seguimiento aparte: el resultado de un grupo es
 * todo-o-nada (si falla cualquier chunk se marcan todos sus pending ids), así que todos los
 * pendientes de la fila fusionada corren su suerte.
 */
function fusionarPorClave(records: Record<string, any>[], pk: string): Record<string, any>[] {
  const porClave = new Map<unknown, Record<string, any>>();
  const resultado: Record<string, any>[] = [];
  for (const record of records) {
    const clave = record[pk];
    if (clave === undefined || clave === null) {
      resultado.push(record);
      continue;
    }
    const previo = porClave.get(clave);
    if (previo) {
      Object.assign(previo, record);
    } else {
      const copia = { ...record };
      porClave.set(clave, copia);
      resultado.push(copia);
    }
  }
  return resultado;
}

/**
 * Procesa todos los cambios pendientes agrupándolos por `table:action`.
 *
 * Comportamiento ante fallo (decisión de diseño): si ALGÚN chunk de un grupo
 * falla, todos los pending ids de ese grupo se marcan como fallidos y se deja
 * que el caller (Background Sync / sync periódico) reintente. No hay fallback
 * registro-por-registro, para uniformar app y SW.
 *
 * @returns los ids pendientes que se deben eliminar (éxito) y los que se deben
 *          marcar con error (fallo).
 */
export async function flushPendingBatch(
  pending: PendingChange[],
  senders: BatchSenders,
  options: FlushOptions
): Promise<FlushResult> {
  const succeededPendingIds: number[] = [];
  const failedPendingIds: number[] = [];
  const groupErrors: Record<string, string> = {};
  const deferredPendingIds: number[] = [];

  if (pending.length === 0) {
    return { succeededPendingIds, failedPendingIds };
  }

  // Agrupar por `${table}:${action}` (clave compuesta).
  const groups = new Map<string, PendingChange[]>();
  for (const change of pending) {
    const key = `${change.table}:${change.action}`;
    let group = groups.get(key);
    if (!group) {
      group = [];
      groups.set(key, group);
    }
    group.push(change);
  }

  // Tablas cuya escritura falló (o se difirió) en esta corrida: sus hijas no se envían.
  const tablasNoEscritas = new Set<string>();

  for (const key of ordenarGruposPorDependencia(Array.from(groups.keys()))) {
    const changes = groups.get(key)!;
    const [table, action] = key.split(':');
    const groupIds = changes.map(c => c.id).filter((id): id is number => id !== undefined);

    if (esGrupoDeEscritura(key) && tablasPadreDeSync(table).some(p => tablasNoEscritas.has(p))) {
      console.warn(`[syncBatcher] ${key} diferido: falló la escritura de una tabla padre en esta corrida`);
      deferredPendingIds.push(...groupIds);
      tablasNoEscritas.add(table);
      continue;
    }
    const pk = TABLE_CONFIG[table]?.primaryKey || 'id';

    // ==================== INSERT / UPDATE / BULK_INSERT (upsert) ====================
    if (action === 'INSERT' || action === 'UPDATE' || action === 'BULK_INSERT') {
      // Aplanar a un único array de registros (los BULK_INSERT ya vienen como arrays)
      const allRecords = changes.flatMap(c =>
        Array.isArray(c.record) ? c.record : [c.record as Record<string, any>]
      );
      // Filtrar registros que no sean objetos (no aplica para INSERT/UPDATE)
      // Una fila tocada varias veces en la cola viaja una sola vez (ver fusionarPorClave).
      const objectRecords = fusionarPorClave(
        allRecords.filter(r => typeof r !== 'string') as Record<string, any>[],
        pk
      );

      if (objectRecords.length === 0) {
        // Nada que sincronizar: contar como éxito y limpiar
        succeededPendingIds.push(...groupIds);
        continue;
      }

      const chunks = chunkArray(objectRecords, options.batchSize);
      let allOk = true;
      let lastError = '';
      for (const chunk of chunks) {
        const outcome = await senders.upsertRecords(table, chunk);
        if (!outcome.ok) {
          allOk = false;
          lastError = outcome.error || 'upsert error';
        }
      }

      if (allOk) {
        succeededPendingIds.push(...groupIds);
      } else {
        console.warn(`[syncBatcher] Batch upsert failed for ${table}:`, lastError);
        failedPendingIds.push(...groupIds);
        tablasNoEscritas.add(table);
        groupErrors[key] = lastError;
      }
    }

    // ==================== DELETE / BULK_DELETE ====================
    else if (action === 'DELETE' || action === 'BULK_DELETE') {
      const ids = changes.flatMap(c => {
        if (Array.isArray(c.record)) return c.record as string[];
        if (typeof c.record === 'string') return [c.record];
        return [(c.record as Record<string, any>)[pk]];
      }).filter(Boolean) as string[];

      if (ids.length === 0) {
        succeededPendingIds.push(...groupIds);
        continue;
      }

      const chunks = chunkArray(ids, options.batchSize);
      let allOk = true;
      let lastError = '';
      for (const chunk of chunks) {
        const outcome = await senders.deleteByIds(table, chunk);
        if (!outcome.ok) {
          allOk = false;
          lastError = outcome.error || 'delete error';
        }
      }

      if (allOk) {
        succeededPendingIds.push(...groupIds);
      } else {
        console.warn(`[syncBatcher] Batch delete failed for ${table}:`, lastError);
        failedPendingIds.push(...groupIds);
        groupErrors[key] = lastError;
      }
    }

    // ==================== BULK_UPDATE (merge upsert) ====================
    else if (action === 'BULK_UPDATE') {
      const records = fusionarPorClave(
        changes.flatMap(c =>
          Array.isArray(c.record) ? c.record as Record<string, any>[] : [c.record as Record<string, any>]
        ).filter(r => r[pk] !== undefined && r[pk] !== null),
        pk
      );

      if (records.length === 0) {
        succeededPendingIds.push(...groupIds);
        continue;
      }

      const chunks = chunkArray(records, options.batchSize);
      let allOk = true;
      let lastError = '';
      for (const chunk of chunks) {
        const outcome = await senders.bulkUpdateRecords(table, chunk);
        if (!outcome.ok) {
          allOk = false;
          lastError = outcome.error || 'bulk update error';
        }
      }

      if (allOk) {
        succeededPendingIds.push(...groupIds);
      } else {
        console.warn(`[syncBatcher] Batch update failed for ${table}:`, lastError);
        failedPendingIds.push(...groupIds);
        tablasNoEscritas.add(table);
        groupErrors[key] = lastError;
      }
    }
  }

  return { succeededPendingIds, failedPendingIds, groupErrors, deferredPendingIds };
}
